import { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder, useMotionValue, animate, type PanInfo } from 'motion/react';
import { ReorderRow } from './ReorderRow';
import { doc, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { cn, deepClean } from '../lib/utils';
import { format } from 'date-fns';
import { battingStyleHasEH, battingOrderSize, generateOptimalLineup } from '../lib/lineup-engine';
import { ORDER_MODES, orderBattingLineup, DEFENSE_MODES, defenseSettingsFor } from '../lib/batting-order';
import { aggregateBatterSignals, BatterSeasonSignal } from '../lib/insights/season-signals';
import { PITCH_COUNT_RULESETS, getPitcherEligibility, buildCustomTournamentRules } from '../lib/pitch-count-rules';
import { saveLocalFirst } from '../lib/save-local-first';
import { PlanGameView } from './PlanGameView';
import { Play, X, UserMinus, AlertTriangle, ChevronDown, Target, GripVertical } from 'lucide-react';

// The swipe-to-reveal "Absent" action lives on this wrapper. Framer's
// dragConstraints only cap travel — they don't pull the row back — so without
// an onDragEnd handler a small accidental swipe would leave the row stuck
// half-open. This snaps to the nearest state on release: past the halfway
// threshold (or a fast flick) latches open so "Absent" is tappable; anything
// less springs back to closed. Extracted to a component so each row gets its
// own useMotionValue (hooks can't run inside the .map()).
const OPEN_X = -80;              // fully-revealed offset (matches the action width)
const SNAP_OPEN_THRESHOLD = -40; // dragged past halfway → latch open
const SwipeableRow = ({ children, style }: { children: React.ReactNode; style: React.CSSProperties }) => {
  const x = useMotionValue(0);
  const handleDragEnd = (_e: unknown, info: PanInfo) => {
    // Open if the user pulled past halfway OR flicked left quickly; else close.
    const shouldOpen = info.offset.x < SNAP_OPEN_THRESHOLD || info.velocity.x < -500;
    animate(x, shouldOpen ? OPEN_X : 0, { type: 'spring', stiffness: 500, damping: 40 });
  };
  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: OPEN_X, right: 0 }}
      dragElastic={0.1}
      onDragEnd={handleDragEnd}
      style={{ ...style, x }}
      className="flex items-center justify-between cursor-grab active:cursor-grabbing relative z-10"
    >
      {children}
    </motion.div>
  );
};

export const LineupBuilder =({ game: initialGame, team, teamId, players, games, tournaments = [], playerSeasonStats, playerHotCold = new Map(), onClose, onPlayBall }: any) => {
  // Always track the live version of this game from the synced games array so that
  // changes saved in PlanGameView (or elsewhere) are immediately reflected here.
  const liveGame = (games as any[]).find((g: any) => g.id === initialGame.id) ?? initialGame;

  const buildLineupFromPlan = (sourceGame: any, absentIds: string[]) => {
    if (!sourceGame.plan?.playerOrder || !sourceGame.plan?.innings?.["0"]) return null;
    const firstInning = sourceGame.plan.innings["0"];
    const plannedLineup = sourceGame.plan.playerOrder.map((id: string) => {
      const player = players.find((p: any) => p.id === id);
      if (!player) return null;
      const posObj = firstInning.find((p: any) => p.id === id);
      return { ...player, position: posObj ? posObj.position : 'BENCH' };
    }).filter(Boolean);
    const remaining = players
      .filter((p: any) => !sourceGame.plan.playerOrder.includes(p.id))
      .map((p: any) => ({ ...p, position: 'BENCH' }));
    return [...plannedLineup, ...remaining].filter((p: any) => !absentIds.includes(p.id));
  };

  // Resolve which players are absent for this game. The Game Plan stores its
  // absences under plan.absentPlayerIds; a live/in-progress game stores them at
  // the top level. Prefer the plan's list when present so absences set in Build
  // Plan flow through to the builder (and into the absent container below).
  const resolveAbsentIds = (sourceGame: any): string[] =>
    sourceGame?.plan?.absentPlayerIds ?? sourceGame?.absentPlayerIds ?? [];

  const [absentPlayerIds, setAbsentPlayerIds] = useState<string[]>(
    resolveAbsentIds((games as any[]).find((g: any) => g.id === initialGame.id) ?? initialGame)
  );
  const [lineup, setLineup] = useState<any[]>(() => {
    // Use liveGame (the Firestore-synced snapshot) so we always get the latest plan
    // even if it was saved moments before the builder opened.
    const source = (games as any[]).find((g: any) => g.id === initialGame.id) ?? initialGame;
    const absentIds = resolveAbsentIds(source);
    // Only use a saved game.lineup if the game is already in progress — otherwise
    // a stale lineup from a previous session would permanently override a fresh plan.
    if (source.status === 'in_progress' && source.lineup && source.lineup.length > 0) return source.lineup;
    const fromPlan = buildLineupFromPlan(source, absentIds);
    if (fromPlan) return fromPlan;
    return [...players]
      .sort((a, b) => (b.ovr || 0) - (a.ovr || 0))
      .map(p => ({ ...p, position: 'BENCH' }))
      .filter((p: any) => !absentIds.includes(p.id));
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingLineup, setIsGeneratingLineup] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  // GameChanger at-bat signals — power the data-driven order modes ('Get
  // Baserunners' / 'Wear Down Pitcher'), same engine as Plan Game's LineupTab.
  const [batterSignals, setBatterSignals] = useState<Map<string, BatterSeasonSignal>>(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const gamesSnap = await getDocs(collection(db, `teams/${teamId}/games`));
        const all: any[] = [];
        await Promise.all(gamesSnap.docs.map(async (g) => {
          const abSnap = await getDocs(collection(db, `teams/${teamId}/games/${g.id}/atBats`));
          abSnap.forEach((d) => all.push({ ...(d.data() as any), gameId: g.id }));
        }));
        if (!cancelled) setBatterSignals(aggregateBatterSignals(all as any));
      } catch { /* no at-bats imported yet — data modes stay disabled */ }
    })();
    return () => { cancelled = true; };
  }, [teamId]);
  const [showFillDropdown, setShowFillDropdown] = useState(false);
  const [showPitcherDropdown, setShowPitcherDropdown] = useState(false);
  // Display label for the batting-order picker (the Builder applies an order on
  // pick but doesn't otherwise track which mode is active). 'None' = Default (xOVR).
  const [orderModeKey, setOrderModeKey] = useState<string>('None');
  // Defense fill mode — same keys and names as the Game Planner and the
  // live-game Auto Lineup menu (shared DEFENSE_MODES).
  const [fillMode, setFillMode] = useState<'smart' | 'competitive' | 'rotate_all'>(
    initialGame.tournamentId ? 'competitive' : 'smart'
  );
  const [showPlanGame, setShowPlanGame] = useState(false);
  // Initialize to null so the plan-sync effect always fires on mount and picks up
  // any plan saved before the builder opened (fixes the case where savedAt matches
  // the initial snapshot and the effect skips silently).
  const [lastSyncedPlanAt, setLastSyncedPlanAt] = useState<string | null>(null);
  const [startingPitcherId, setStartingPitcherId] = useState<string>(() => {
    const p = lineup.find(p => p.position === 'P');
    return p ? p.id : '';
  });
  const [alertModal, setAlertModal] = useState<{ title: string; message: string } | null>(null);

  // Game clock prompt flow (opt-in countdown for timed games)
  const [clockIntro, setClockIntro] = useState(false);     // first-use "Do you want a timer?"
  const [clockPrompt, setClockPrompt] = useState(false);   // duration entry
  const [clockNotice, setClockNotice] = useState(false);   // "turn it back on in Settings" notice
  const [clockMinutes, setClockMinutes] = useState<string>(String(team.settings?.defaultGameClockMinutes ?? 75));

  // Live plan sync: whenever the game's plan changes in Firestore (caught by the
  // parent's onSnapshot listener), re-ingest Inning 0 into the builder so that
  // work done in "Build Plan" is never lost. Also fires on mount (lastSyncedPlanAt
  // starts null) so a plan saved just before opening the builder is always applied.
  useEffect(() => {
    const planSavedAt = liveGame.plan?.savedAt ?? null;
    if (!planSavedAt || planSavedAt === lastSyncedPlanAt) return;
    // Don't re-apply the plan once the game is actually in progress — the live
    // lineup is now the source of truth.
    if (liveGame.status === 'in_progress') return;

    // Pull the plan's absences in too, so players marked absent in Build Plan
    // land in the absent container here (and out of the batting order).
    const planAbsentIds = resolveAbsentIds(liveGame);
    const fromPlan = buildLineupFromPlan(liveGame, planAbsentIds);
    if (fromPlan) {
      setAbsentPlayerIds(planAbsentIds);
      setLineup(fromPlan);
      const pitcher = fromPlan.find((p: any) => p.position === 'P');
      if (pitcher) setStartingPitcherId(pitcher.id);
      setLastSyncedPlanAt(planSavedAt);
    }
  }, [liveGame.plan?.savedAt]);

  const availablePositions = [
    ...(team.settings?.noPitcher ? [] : ['P']),
    ...(team.settings?.noCatcher ? [] : ['C']),
    '1B', '2B', '3B', 'SS', 'LF',
    ...(team.settings?.fourOutfielders ? ['LCF', 'RCF'] : ['CF']),
    'RF',
    ...(battingStyleHasEH(team.settings) ? ['EH'] : []),
    'BENCH'
  ];

  const isPlayersPitch = team.styleOfPlay === 'playerspitch';

  // Persist the game-clock feature flags onto the team doc (dot-path so we don't
  // clobber the rest of settings). Non-fatal — the immediate flow doesn't depend
  // on the write landing, so it isn't awaited (with no signal it would never settle).
  const persistClockSettings = (patch: Record<string, any>) => {
    updateDoc(doc(db, 'teams', teamId),
      Object.fromEntries(Object.entries(patch).map(([k, v]) => [`settings.${k}`, v])))
      .catch(() => { /* non-fatal */ });
  };

  // Actually start the game. `minutes` > 0 attaches a countdown clock to the game.
  const commitStart = async (minutes: number | null) => {
    setIsSaving(true);
    try {
      const clockFields: any = {};
      if (minutes && minutes > 0) {
        clockFields.clockDurationMin = minutes;
        clockFields.clockEndsAt = new Date(Date.now() + minutes * 60000).toISOString();
      }
      const startedGame = deepClean({ ...liveGame, lineup, absentPlayerIds, status: 'in_progress', ...clockFields });
      // Play Ball must work at a field with no signal: move on once the start is
      // saved on this phone, it uploads when the connection returns.
      await saveLocalFirst(setDoc(doc(db, `teams/${teamId}/games`, liveGame.id), startedGame, { merge: true }), 'Play Ball');
      // The Lock Screen activity (clock + Pocket Pager) starts when the live
      // screen opens on startedGame, which carries clockEndsAt.
      onPlayBall(startedGame);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `teams/${teamId}/games/${liveGame.id}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClockIntroYes = async () => {
    setClockIntro(false);
    persistClockSettings({ gameClockEnabled: true, gameClockPromptSeen: true });
    setClockMinutes(String(team.settings?.defaultGameClockMinutes ?? 75));
    setClockPrompt(true);
  };
  const handleClockIntroNo = async () => {
    setClockIntro(false);
    persistClockSettings({ gameClockEnabled: false, gameClockPromptSeen: true });
    setClockNotice(true);
  };
  const handleClockConfirm = async () => {
    const m = parseInt(clockMinutes) || 0;
    setClockPrompt(false);
    await commitStart(m > 0 ? m : null);
  };
  const handleClockSkip = async () => {
    setClockPrompt(false);
    await commitStart(null);
  };
  const handleClockNoticeDismiss = async () => {
    setClockNotice(false);
    await commitStart(null);
  };

  const handleSave = async () => {
    // Only require a pitcher if it's a Players Pitch style
    const pitcher = lineup.find(p => p.position === 'P');
    if (isPlayersPitch && !pitcher && !team.settings?.noPitcher) {
      setAlertModal({
        title: 'Starting Pitcher Needed',
        message: 'Pick a starting pitcher before the first pitch. Tap Starting Pitcher at the top to choose one.',
      });
      setShowPitcherDropdown(true);
      return;
    }

    // Game clock gating:
    //  - never asked before → one-time opt-in prompt
    //  - feature on         → ask the duration for this game
    //  - feature off        → start with no clock (unchanged behavior)
    if (!team.settings?.gameClockPromptSeen) {
      setClockIntro(true);
      return;
    }
    if (team.settings?.gameClockEnabled) {
      setClockMinutes(String(team.settings?.defaultGameClockMinutes ?? 75));
      setClockPrompt(true);
      return;
    }
    await commitStart(null);
  };

  const updatePosition = (playerId: string, pos: string) => {
    if (pos === 'P') {
      setStartingPitcherId(playerId);
    } else if (playerId === startingPitcherId) {
      setStartingPitcherId('');
    }

    if (pos === 'P') {
      const player = lineup.find(p => p.id === playerId);
      
      // Check re-entry rule
      if (team.settings?.noPitcherReentry && liveGame.inningLogs) {
        const innings = Object.keys(liveGame.inningLogs).map(Number).sort((a, b) => a - b);
        const activeInning = innings.length > 0 ? Math.max(...innings) : 0;
        
        let hasPitchedPreviously = false;
        Object.entries(liveGame.inningLogs).forEach(([inningNum, log]: [string, any]) => {
          if (Number(inningNum) < activeInning) {
            const playerLog = log.find((l: any) => l.id === player.id);
            if (playerLog?.position === 'P') {
              hasPitchedPreviously = true;
            }
          }
        });

        if (hasPitchedPreviously) {
          const lastInning = activeInning - 1;
          const lastInningLog = liveGame.inningLogs[lastInning];
          const wasPitchingLastInning = lastInningLog?.find((l: any) => l.id === player.id)?.position === 'P';
          
          if (!wasPitchingLastInning) {
            setAlertModal({
              title: 'Illegal Re-entry',
              message: `${player.name} has already pitched in this game and cannot return to the mound.`,
            });
            return;
          }
        }
      }

      const playerFull = players.find((pl: any) => pl.id === playerId);
      const pitchLog: any[] = playerFull?.pitchingLog || [];
      if (pitchLog.length > 0) {
        const sorted = [...pitchLog].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const lastEntry = sorted[0];
        if (lastEntry?.eligibleDate) {
          const gameDate = new Date(liveGame.date);
          gameDate.setHours(0, 0, 0, 0);
          const eligible = new Date(lastEntry.eligibleDate);
          eligible.setHours(0, 0, 0, 0);
          if (eligible > gameDate) {
            const dateStr = eligible.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            setAlertModal({
              title: 'Pitcher on Mandatory Rest',
              message: `${playerFull.name} is not available to pitch until ${dateStr}. You can still assign them, but this violates pitch rest rules.`
            });
          }
        }
      }
    }
    // One player per fielding position, always. Whoever held the slot is swapped
    // into the incoming player's old spot (a true swap, the way a coach thinks
    // about it), rather than being left there to create a duplicate. BENCH and
    // EH are the exceptions: any number of players can sit.
    const incoming = lineup.find(p => p.id === playerId);
    const vacated = incoming?.position ?? 'BENCH';
    const shared = pos === 'BENCH' || pos === 'EH';
    setLineup(lineup.map(p => {
      if (p.id === playerId) return { ...p, position: pos };
      if (!shared && p.position === pos) return { ...p, position: vacated };
      return p;
    }));
  };

  const autoGenerate = (mode: 'smart' | 'competitive' | 'rotate_all' = fillMode) => {
    setIsGeneratingLineup(true);

    setTimeout(() => {
      try {
        const settings = team.settings || {};

        // Only include players who are NOT absent and mark the current pitcher.
        // Enrich with live season stats and ovr so the engine uses real data, not stale lineup state.
        let availablePlayers = lineup.filter(p => !absentPlayerIds.includes(p.id)).map(p => {
          const seasonStats = playerSeasonStats.get(p.id) ?? null;
          const livePlayer  = players.find((pl: any) => pl.id === p.id);
          return {
            ...p,
            stats: seasonStats ?? p.stats,
            ovr:   livePlayer?.ovr ?? p.ovr,
            isCurrentPitcher: p.id === startingPitcherId,
          };
        });
        
        // Use the manually selected pitcher if available
        const currentInningLogs = { ...liveGame.inningLogs };
        
        // Generate positions using the shared engine. The Builder sets the
        // inning-1 starting defense, so only smart/competitive apply here; the
        // multi-inning modes are disabled in the menu and never reach this.
        const effectiveSettings = defenseSettingsFor(mode, settings);
        const { lineup: generatedLineup, warnings, pitcherGap } = generateOptimalLineup(
          availablePlayers,
          effectiveSettings,
          currentInningLogs,
          liveGame.date,
          liveGame.tournamentId,
          team.styleOfPlay
        );

        // The coach hasn't named a starter yet, so confirm the mound before the
        // defense is locked in. Three cases, three different asks:
        //   suggested - the engine put its best arm out there; name them and let
        //               the coach change it. The generated defense is kept.
        //   unrated   - nobody is rated to pitch, so there is nothing to suggest.
        //   blocked   - arms exist but rest/re-entry rules sit them all down.
        // Only the last two stop the fill, because only they leave a real hole.
        if (pitcherGap && !startingPitcherId) {
          if (pitcherGap.reason === 'suggested') {
            const suggested = generatedLineup.find((p: any) => p.position === 'P');
            if (suggested) setStartingPitcherId(suggested.id);
            setAlertModal({
              title: 'Confirm Your Starter',
              message: `${pitcherGap.message} Tap Starting Pitcher at the top to go with someone else.`,
            });
          } else {
            setAlertModal({
              title: pitcherGap.reason === 'unrated' ? 'Pick a Starting Pitcher' : 'No Eligible Pitchers',
              message: `${pitcherGap.message} Tap Starting Pitcher above to choose one, then Dugout will fill in the rest of the defense around them.`,
            });
            setShowPitcherDropdown(true);
            return;
          }
        }

        if (warnings.length > 0) {
          setAlertModal({ title: 'Lineup Warning', message: warnings.join('\n') });
        }

        // If a pitcher was manually set, we want to keep that assignment
        const finalGeneratedLineup = [...generatedLineup];
        if (startingPitcherId) {
          const manualPitcher = finalGeneratedLineup.find(p => p.id === startingPitcherId);
          const currentPitcher = finalGeneratedLineup.find(p => p.position === 'P');
          
          if (manualPitcher && currentPitcher && manualPitcher.id !== currentPitcher.id) {
            // Swap positions: P goes back to Bench or manualPitcher's suggested spot, manualPitcher becomes P
            const suggestedPosForManual = manualPitcher.position;
            currentPitcher.position = suggestedPosForManual === 'P' ? 'BENCH' : suggestedPosForManual;
            manualPitcher.position = 'P';
          }
        }
        // Merge back with absent players
        let newLineup = lineup.map(p => {
          if (absentPlayerIds.includes(p.id)) {
            return { ...p, position: 'ABSENT' };
          }
          
          const generatedPlayer = finalGeneratedLineup.find(n => n.id === p.id);
          let pos = generatedPlayer ? generatedPlayer.position : p.position;

          // Force manual pitcher preservation
          if (startingPitcherId) {
            if (p.id === startingPitcherId) {
              pos = 'P';
            } else if (pos === 'P') {
              // If engine assigned a different pitcher, bump them to BENCH
              pos = 'BENCH';
            }
          }

          return { ...(generatedPlayer || p), position: pos };
        });

        // Re-apply sort from current lineup to maintain batting order
        // The lineup builder uses the order of elements in the array for the batting order.
        const orderedNewLineup: any[] = [];
        for (const player of lineup) {
            const updatedPlayer = newLineup.find(p => p.id === player.id);
            if (updatedPlayer) {
                orderedNewLineup.push(updatedPlayer);
            }
        }

        setLineup(orderedNewLineup);
      } catch (err) {
        console.error("Auto-generate failed", err);
      } finally {
        setIsGeneratingLineup(false);
      }
    }, 100);
  };

  const optimizeOrder = (strategy: string) => {
    setIsGeneratingLineup(true);
    setShowOrderDropdown(false);
    setTimeout(() => {
      try {
        // Enrich players with fresh season stats before sorting (same as Fill Defense does)
        const enrichedLineup = lineup.map(p => {
          const seasonStats = playerSeasonStats.get(p.id) ?? null;
          const livePlayer = players.find((pl: any) => pl.id === p.id);
          return {
            ...p,
            stats: seasonStats ?? p.stats,
            ovr: livePlayer?.ovr ?? p.ovr,
          };
        });

        const absentPlayers = enrichedLineup.filter(p => absentPlayerIds.includes(p.id));
        const presentPlayers = enrichedLineup.filter(p => !absentPlayerIds.includes(p.id));
        // Unified engine — shared with Plan Game's LineupTab so both surfaces
        // produce identical orders for the same mode.
        const ordered = orderBattingLineup(presentPlayers, batterSignals, strategy).map(o => o.player);
        setLineup([...ordered, ...absentPlayers]);
      } finally {
        setIsGeneratingLineup(false);
      }
    }, 100);
  };

  const toggleAbsence = (playerId: string) => {
    const isAbsent = absentPlayerIds.includes(playerId);
    let newAbsentIds;
    if (isAbsent) {
      newAbsentIds = absentPlayerIds.filter(id => id !== playerId);
      // Add back to lineup if they were missing
      if (!lineup.find(p => p.id === playerId)) {
        const player = players.find((p: any) => p.id === playerId);
        if (player) {
          setLineup([...lineup, { ...player, position: 'BENCH' }]);
        }
      }
    } else {
      newAbsentIds = [...absentPlayerIds, playerId];
      // Remove from lineup
      setLineup(lineup.filter(p => p.id !== playerId));
    }
    setAbsentPlayerIds(newAbsentIds);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 bg-surface flex flex-col safe-top safe-x"
    >
      <header className="sticky top-0 z-20" style={{ backgroundColor: '#FFFFFF', borderBottom: '0.5px solid #E0DED9' }}>
        {/* Top row: close + game identity */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          <button
            onClick={onClose}
            style={{ padding: '6px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#5A6672', display: 'flex', flexShrink: 0 }}
            aria-label="Close"
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>

          <div className="flex-1 min-w-0">
            <h2 style={{ fontSize: '18px', fontWeight: 800, fontStyle: 'italic', textTransform: 'uppercase', lineHeight: 1.1, color: '#0D0D0D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {liveGame.isHome ? 'vs ' : '@ '}{liveGame.opponent}
            </h2>
            <p style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: '2px' }}>
              {format(new Date(liveGame.date), 'EEE, MMM d')}
            </p>
          </div>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 16px 12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowPlanGame(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', padding: '6px 12px', border: '0.5px solid #E0DED9', borderRadius: '8px', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#0D0D0D' }}
          >
            <Target style={{ width: '14px', height: '14px' }} />
            Build Plan
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto bg-surface">
        <div className="max-w-md mx-auto px-4 py-4 space-y-4">

        {/* ── Setup card — mirrors Game Plan ─────────────────────────────── */}
        <div style={{ border: '0.5px solid #E0DED9', borderRadius: '12px', overflow: 'visible' }}>

          {/* Section 1: Starting Pitcher (Players Pitch only) */}
          {isPlayersPitch && (
            <div style={{ position: 'relative', borderRadius: '12px 12px 0 0' }}>
              {showPitcherDropdown && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowPitcherDropdown(false)} />
              )}
              <div style={{ position: 'relative', zIndex: 20 }}>
                {showPitcherDropdown ? (
                  <div className="scroll-hint" style={{ backgroundColor: '#FFFFFF', borderBottom: '0.5px solid #E0DED9', borderRadius: '12px 12px 0 0', maxHeight: '220px', overflowY: 'auto' }}>
                    {players
                      .filter((p: any) => !absentPlayerIds.includes(p.id))
                      .map((p: any) => {
                        const tournament = tournaments.find((t: any) => t.id === liveGame.tournamentId);
                        // Tournament-aware ruleset: a tournament's override (e.g. 'custom')
                        // wins over the season ruleset, so per-tournament custom rules apply.
                        const rulesetKey = (liveGame.tournamentId
                          ? (tournament?.pitchCountRulesetOverride || team.settings?.tournamentOrg)
                          : null) || team.settings?.pitchCountRuleset || 'little_league';
                        const ruleset = PITCH_COUNT_RULESETS[rulesetKey];
                        const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
                        const enforceGlobal = tournament?.enforceGlobalRest ?? team.settings?.enforceGlobalRest ?? true;
                        const effectiveCustomRules = buildCustomTournamentRules(ruleset?.id === 'custom', tournament, team.settings);
                        const eligibility = ruleset ? getPitcherEligibility(
                          p.pitchingLog || [], ruleset, ageGroup, new Date(liveGame.date), 0,
                          liveGame.tournamentId, enforceGlobal, effectiveCustomRules
                        ) : { eligible: true, pitchesRemaining: 999, dailyMax: 999 };
                        return (
                          <button
                            key={p.id}
                            disabled={!eligibility.eligible}
                            onClick={() => { updatePosition(p.id, 'P'); setShowPitcherDropdown(false); }}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
                              fontSize: '15px', fontWeight: startingPitcherId === p.id ? 600 : 400,
                              color: !eligibility.eligible ? '#C8C5BF' : startingPitcherId === p.id ? '#0D0D0D' : '#141A21',
                              background: startingPitcherId === p.id ? '#FAFAFA' : 'transparent',
                              border: 'none', borderBottom: '0.5px solid #E0DED9',
                              cursor: eligibility.eligible ? 'pointer' : 'not-allowed',
                            }}
                          >
                            #{p.number} {p.name}{!eligibility.eligible ? ' · Rest required' : ''}
                          </button>
                        );
                      })}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowPitcherDropdown(true)}
                    style={{
                      display: 'flex', alignItems: 'center', width: '100%', padding: '13px 14px',
                      background: 'none', border: 'none', borderBottom: '0.5px solid #E0DED9',
                      cursor: 'pointer', gap: '12px', borderRadius: '12px 12px 0 0',
                    }}
                  >
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: startingPitcherId ? '#0D0D0D' : '#F2F0EB',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: 600, color: startingPitcherId ? '#FFFFFF' : '#5A6672',
                    }}>
                      {startingPitcherId ? `#${players.find((p: any) => p.id === startingPitcherId)?.number}` : '?'}
                    </div>
                    <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '2px' }}>Starting Pitcher</div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: startingPitcherId ? '#0D0D0D' : '#5A6672', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {startingPitcherId ? players.find((p: any) => p.id === startingPitcherId)?.name : 'Tap to select'}
                      </div>
                    </div>
                    <ChevronDown style={{ width: '18px', height: '18px', color: '#C8C5BF', flexShrink: 0 }} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Section 2: Batting Order + Defense */}
          <div style={{ display: 'flex', borderRadius: isPlayersPitch ? '0 0 12px 12px' : '12px', overflow: 'visible' }}>
            {/* Batting Order column */}
            <div style={{ flex: 1, padding: '12px 14px', borderRight: '0.5px solid #E0DED9' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Batting Order</div>
              <div style={{ position: 'relative' }}>
                {showOrderDropdown && (
                  <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowOrderDropdown(false)} />
                )}
                {showOrderDropdown ? (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50, backgroundColor: '#FFFFFF', border: '0.5px solid #E0DED9', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                    {ORDER_MODES.map(({ key, label, data }) => {
                      const disabled = data && batterSignals.size === 0;
                      return (
                        <button
                          key={key}
                          disabled={disabled}
                          onClick={() => { setOrderModeKey(key); optimizeOrder(key); setShowOrderDropdown(false); }}
                          title={disabled ? 'Import a GameChanger scorebook to unlock this data-driven order.' : undefined}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', fontSize: '13px',
                            fontWeight: orderModeKey === key ? 600 : 400,
                            color: disabled ? '#C8C5BF' : orderModeKey === key ? '#0D0D0D' : '#5A6672',
                            backgroundColor: orderModeKey === key ? '#FAFAFA' : 'transparent',
                            border: 'none', borderBottom: '0.5px solid #E0DED9',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}
                        >{key === 'None' ? 'Default (xOVR)' : label}</button>
                      );
                    })}
                  </div>
                ) : (
                  <button
                    onClick={() => { if (!isGeneratingLineup && !(isPlayersPitch && !startingPitcherId)) setShowOrderDropdown(true); }}
                    disabled={isGeneratingLineup || (isPlayersPitch && !startingPitcherId)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer', opacity: (isGeneratingLineup || (isPlayersPitch && !startingPitcherId)) ? 0.5 : 1 }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#0D0D0D' }}>
                      {isGeneratingLineup ? 'Working…' : (orderModeKey === 'None' ? 'Default (xOVR)' : (ORDER_MODES.find(m => m.key === orderModeKey)?.label || 'Default (xOVR)'))}
                    </span>
                    <ChevronDown style={{ width: '14px', height: '14px', color: '#C8C5BF' }} />
                  </button>
                )}
              </div>
            </div>

            {/* Defense column */}
            <div style={{ flex: 1, padding: '12px 14px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Defense</div>
              <div style={{ position: 'relative' }}>
                {showFillDropdown && (
                  <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowFillDropdown(false)} />
                )}
                {showFillDropdown ? (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50, backgroundColor: '#FFFFFF', border: '0.5px solid #E0DED9', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                    {DEFENSE_MODES.map(({ key, label, description }) => (
                      <button
                        key={key}
                        onClick={() => { setFillMode(key); setShowFillDropdown(false); autoGenerate(key); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px',
                          backgroundColor: fillMode === key ? '#FAFAFA' : 'transparent',
                          border: 'none', borderBottom: '0.5px solid #E0DED9', cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: fillMode === key ? 600 : 400, color: fillMode === key ? '#0D0D0D' : '#5A6672' }}>
                          {label}
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 400, color: '#8B919A', marginTop: '2px' }}>
                          {description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => { if (!isGeneratingLineup && !(isPlayersPitch && !startingPitcherId)) setShowFillDropdown(true); }}
                    disabled={isGeneratingLineup || (isPlayersPitch && !startingPitcherId)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer', opacity: (isGeneratingLineup || (isPlayersPitch && !startingPitcherId)) ? 0.5 : 1 }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#0D0D0D' }}>
                      {isGeneratingLineup ? 'Filling…' : (DEFENSE_MODES.find(m => m.key === fillMode)?.label || 'Equal Bench Time')}
                    </span>
                    <ChevronDown style={{ width: '14px', height: '14px', color: '#C8C5BF' }} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={cn(
          "space-y-3 transition-all duration-500",
          (isPlayersPitch && !startingPitcherId) ? "opacity-30 blur-[2px] pointer-events-none scale-[0.98]" : "opacity-100 blur-0"
        )}>
            <div className="flex items-center justify-between" style={{ padding: '0 2px' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Batting Order ({lineup.length})</span>
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Drag to reorder</span>
            </div>

            <Reorder.Group 
              axis="y" 
              values={lineup.map(p => p.id)} 
              onReorder={(newIds) => {
                const reordered = newIds.map(id => lineup.find(p => p.id === id)).filter(Boolean);
                setLineup(reordered);
              }}
              className="space-y-1.5"
            >
            {lineup.map((player, index) => {
              // Fixed-size styles (10 / 9 Players) cap the batting order and show
              // a Substitutes divider; Continuous bats everyone present.
              const maxBatters = battingOrderSize(team.settings, lineup.length);
              const isFixedSize = maxBatters < lineup.length;
              const isSubstitute = isFixedSize && index >= maxBatters;

              return (
                <ReorderRow
                  key={player.id}
                  value={player.id}
                >
                  {(dragControls) => (<>
                  {isFixedSize && index === maxBatters && (
                    <div className="py-3 flex items-center space-x-3">
                      <div className="h-px flex-1" style={{ backgroundColor: '#E0DED9' }} />
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Substitutes</span>
                      <div className="h-px flex-1" style={{ backgroundColor: '#E0DED9' }} />
                    </div>
                  )}

                  <div className="relative rounded-xl overflow-hidden group">
                    {/* Absent Action (Revealed on Swipe) */}
                    <div className="absolute inset-0 flex items-center justify-end pr-6" style={{ backgroundColor: '#E5484D' }}>
                      <button
                        onClick={() => toggleAbsence(player.id)}
                        className="flex flex-col items-center space-y-1 text-ink"
                      >
                        <UserMinus className="w-5 h-5" />
                        <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' }}>Absent</span>
                      </button>
                    </div>

                    <SwipeableRow
                      style={{
                        backgroundColor: '#FFFFFF',
                        padding: '11px 12px',
                        borderRadius: '12px',
                        border: '0.5px solid #E0DED9',
                        borderLeft: player.id === startingPitcherId ? '3px solid #10B981' : '3px solid transparent',
                        opacity: isSubstitute ? 0.5 : 1,
                      }}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#5A6672', width: '16px', textAlign: 'center', flexShrink: 0 }}>
                          {isSubstitute ? '-' : index + 1}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 400, color: '#5A6672', width: '34px', flexShrink: 0 }}>#{player.number}</span>
                        <div className="flex flex-col flex-1 min-w-0 pr-2">
                          <h3 className="flex items-center space-x-2" style={{ fontSize: '15px', fontWeight: 600, color: '#0D0D0D' }}>
                            <span className="truncate">{player.name}</span>
                            {player.id === startingPitcherId && (
                              <span style={{ fontSize: '10px', fontWeight: 600, color: '#0B5C3F', backgroundColor: '#E6F4EE', padding: '1px 5px', borderRadius: '4px' }}>SP</span>
                            )}
                            {(() => {
                              const hc = playerHotCold.get(player.id);
                              const status = isSubstitute ? hc?.pitching : hc?.batting;
                              return status === 'hot' ? <span>🔥</span> : status === 'cold' ? <span>❄️</span> : null;
                            })()}
                            {(() => {
                              const tournament = tournaments.find((t: any) => t.id === liveGame.tournamentId);
                              const rulesetKey = (liveGame.tournamentId
                                ? (tournament?.pitchCountRulesetOverride || team.settings?.tournamentOrg)
                                : null) || team.settings?.pitchCountRuleset || 'little_league';
                              const ruleset = PITCH_COUNT_RULESETS[rulesetKey];
                              const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
                              const enforceGlobal = tournament?.enforceGlobalRest ?? team.settings?.enforceGlobalRest ?? true;
                              const effectiveCustomRules = buildCustomTournamentRules(ruleset?.id === 'custom', tournament, team.settings);

                              const eligibility = ruleset ? getPitcherEligibility(
                                player.pitchingLog || [],
                                ruleset,
                                ageGroup,
                                new Date(liveGame.date),
                                0,
                                liveGame.tournamentId,
                                enforceGlobal,
                                effectiveCustomRules
                              ) : { eligible: true, pitchesRemaining: 999, dailyMax: 999 };
                              if (!eligibility.eligible && eligibility.restStatus) {
                                return (
                                  <div className="group/alert relative flex-shrink-0">
                                    <AlertTriangle className="w-3 h-3 text-gold-ink" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-surface border border-hairline rounded text-[8px] font-bold text-ink-2 uppercase tracking-widest whitespace-nowrap opacity-0 group-hover/alert:opacity-100 transition-opacity pointer-events-none z-50">
                                      Rest until {format(new Date(eligibility.restStatus.eligibleDate), 'MMM d')}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </h3>

                          <div className="flex items-center space-x-2 overflow-hidden" style={{ marginTop: '2px' }}>
                            {(() => {
                              const resolved = players.find((pl: any) => pl.id === player.id) || player;
                              const hasXOVR = resolved.ovrBreakdown?.confidenceLevel && resolved.ovrBreakdown.confidenceLevel !== 'none';
                              return (
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#0B5C3F', flexShrink: 0 }}>
                                  {resolved.ovr || 0}{hasXOVR ? 'x' : ''}
                                </span>
                              );
                            })()}

                            {(() => {
                              const s = playerSeasonStats.get(player.id);
                              if (!s || s.pa === 0) return null;
                              return (
                                <span className="flex flex-wrap items-center truncate" style={{ fontSize: '11px', fontWeight: 400, color: '#5A6672' }}>
                                  <span>{s.avg > 0 ? s.avg.toFixed(3).replace(/^0/, '') : '.---'}</span>
                                  <span style={{ color: '#C8C5BF', margin: '0 4px' }}>/</span>
                                  <span>{s.obp > 0 ? s.obp.toFixed(3).replace(/^0/, '') : '.---'}</span>
                                  <span style={{ color: '#C8C5BF', margin: '0 4px' }}>/</span>
                                  <span>{s.ops > 0 ? s.ops.toFixed(3).replace(/^0/, '') : '.---'}</span>
                                </span>
                              );
                            })()}

                            {player.primaryPosition && (
                              <span className="hidden sm:inline-block ml-auto flex-shrink-0" style={{ fontSize: '11px', fontWeight: 400, color: '#5A6672' }}>
                                {player.primaryPosition}
                              </span>
                            )}
                          </div>
                        </div>
                    </div>

                    <div className="flex items-center flex-shrink-0">
                      <select
                        value={player.position}
                        onChange={(e) => updatePosition(player.id, e.target.value)}
                        disabled={player.id === startingPitcherId}
                        style={{
                          backgroundColor: '#FAFAFA',
                          border: '0.5px solid #E0DED9',
                          borderRadius: '8px',
                          padding: '7px 8px',
                          fontSize: '13px',
                          fontWeight: 600,
                          textAlign: 'center',
                          outline: 'none',
                          appearance: 'none',
                          minWidth: '64px',
                          cursor: player.id === startingPitcherId ? 'not-allowed' : 'pointer',
                          opacity: player.id === startingPitcherId ? 0.5 : 1,
                          color: player.id === startingPitcherId ? '#C9A84C'
                            : player.position !== 'BENCH' ? '#0D0D0D' : '#AAAAAA',
                        }}
                      >
                        {availablePositions
                          .filter(pos => pos !== 'P' || player.id === startingPitcherId)
                          .map(pos => (
                          <option key={pos} value={pos}>{pos}</option>
                        ))}
                      </select>
                    </div>

                    {/* Drag handle — reorder starts ONLY here so a vertical
                        flick anywhere else on the row scrolls the page. touch-none
                        keeps the browser from claiming the gesture as a scroll. */}
                    <div
                      onPointerDown={(e) => dragControls.start(e)}
                      className="flex items-center justify-center flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
                      style={{ paddingLeft: '8px', paddingRight: '2px', alignSelf: 'stretch' }}
                      aria-label="Drag to reorder"
                    >
                      <GripVertical style={{ width: '18px', height: '18px', color: '#C2CBD4' }} />
                    </div>
                  </SwipeableRow>
                  </div>
                  </>)}
                </ReorderRow>
              );
            })}
          </Reorder.Group>

          {absentPlayerIds.length > 0 && (
            <div className="space-y-1.5 pt-5" style={{ borderTop: '0.5px solid #E0DED9' }}>
              <h3 style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Absent Players ({absentPlayerIds.length})</h3>
              {Array.from(new Set(absentPlayerIds)).map(id => {
                const player = players.find((p: any) => p.id === id);
                if (!player) return null;
                return (
                  <div key={id} className="flex items-center justify-between" style={{ backgroundColor: '#FFFFFF', padding: '11px 12px', borderRadius: '12px', border: '0.5px solid #E0DED9', opacity: 0.6 }}>
                    <div className="flex items-center space-x-3">
                      <span style={{ fontSize: '12px', fontWeight: 400, color: '#AAAAAA', width: '34px' }}>#{player.number}</span>
                      <div>
                        <h3 className="flex items-center space-x-1.5" style={{ fontSize: '15px', fontWeight: 600, color: '#888888', textDecoration: 'line-through' }}>
                          <span>{player.name}</span>
                        </h3>
                        <span style={{ fontSize: '11px', fontWeight: 400, color: '#AAAAAA' }}>Not in game</span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAbsence(id)}
                      style={{ fontSize: '12px', fontWeight: 600, color: '#0B5C3F', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Present
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>

      <div style={{ padding: '14px 16px', borderTop: '0.5px solid #E0DED9', backgroundColor: '#FFFFFF' }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            width: '100%', padding: '15px 0', backgroundColor: '#0D0D0D', color: '#FFFFFF',
            fontWeight: 800, fontStyle: 'italic', textTransform: 'uppercase', borderRadius: '12px',
            border: 'none', cursor: isSaving ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          <Play className="w-5 h-5 fill-current" />
          <span>{isSaving ? 'Saving…' : 'Play Ball'}</span>
        </button>
      </div>

      <AnimatePresence>
        {showPlanGame && (
          <PlanGameView
            game={liveGame}
            team={team}
            players={players}
            onClose={() => setShowPlanGame(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {alertModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-surface/70 backdrop-blur-sm px-6"
            onClick={() => setAlertModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-canvas border border-hairline rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-sm font-black uppercase tracking-widest text-gold-ink mb-3">{alertModal.title}</h3>
              <p className="text-sm text-ink-2 leading-relaxed mb-5">{alertModal.message}</p>
              <button
                onClick={() => setAlertModal(null)}
                className="w-full py-3 bg-raised hover:bg-hairline text-ink text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* Game clock — first-use opt-in */}
        {clockIntro && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-surface/70 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-canvas border border-ink-mute rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            >
              <h3 className="text-sm font-black uppercase tracking-widest text-accent-ink mb-3">Do you want to set a game timer?</h3>
              <p className="text-sm text-ink-2 leading-relaxed mb-5">Most youth games are timed. Dugout can count it down here in the app and, on iPhone, right in the Dynamic Island.</p>
              <div className="flex gap-3">
                <button onClick={handleClockIntroNo} className="flex-1 py-3 bg-raised hover:bg-hairline text-ink text-xs font-black uppercase tracking-widest rounded-xl transition-colors">No</button>
                <button onClick={handleClockIntroYes} className="flex-1 py-3 bg-ink hover:bg-ink text-surface text-xs font-black uppercase tracking-widest rounded-xl transition-colors">Yes</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Game clock — duration entry */}
        {clockPrompt && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-surface/70 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-canvas border border-ink-mute rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            >
              <h3 className="text-sm font-black uppercase tracking-widest text-accent-ink mb-3">Game clock</h3>
              <p className="text-sm text-ink-2 leading-relaxed mb-4">How long is this game? The clock counts down from here.</p>
              <div className="flex items-center gap-3 mb-5">
                <input
                  type="number" inputMode="numeric" value={clockMinutes}
                  onChange={e => setClockMinutes(e.target.value)}
                  className="w-24 bg-surface border border-ink-mute rounded-xl px-4 py-3 text-ink text-lg font-black text-center focus:outline-none focus:border-ink-mute"
                />
                <span className="text-xs font-semibold uppercase tracking-widest text-ink-2">minutes</span>
              </div>
              <div className="flex gap-3">
                <button onClick={handleClockSkip} className="flex-1 py-3 bg-raised hover:bg-hairline text-ink text-xs font-black uppercase tracking-widest rounded-xl transition-colors">Skip</button>
                <button onClick={handleClockConfirm} className="flex-1 py-3 bg-ink hover:bg-ink text-surface text-xs font-black uppercase tracking-widest rounded-xl transition-colors">Start</button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Game clock — declined notice */}
        {clockNotice && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-surface/70 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-canvas border border-ink-mute rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            >
              <h3 className="text-sm font-black uppercase tracking-widest text-ink-2 mb-3">Game clock off</h3>
              <p className="text-sm text-ink-2 leading-relaxed mb-5">No problem. You can always turn this back on in Settings.</p>
              <button onClick={handleClockNoticeDismiss} className="w-full py-3 bg-ink hover:bg-ink text-surface text-xs font-black uppercase tracking-widest rounded-xl transition-colors">Got it, Play Ball</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
